/**
 * Status styles for QC verdicts.
 *
 * Two variants:
 * - per-check (`QcResultStatus`): pass / warn / fail / skip
 * - run-level (`QcOverallStatus`): pass / warn / fail
 *
 * Modelled on `components/collections/collection-request-status-badge/`
 * — same StatusStyle shape, same Tailwind palette conventions, same
 * light/dark fork. Reuse the existing CollectionRequestStatusBadge
 * component (or anything compatible with the StatusStyle shape) to
 * render these.
 */

import type { TFunction } from 'i18next'

import type { QcOverallStatus, QcResultStatus } from '../../../types/qc'

export interface StatusStyle {
  label: string
  bg: string
  text: string
  dot: string
}

export function buildResultStatusConfig(
  t: TFunction<'qc'>
): Record<QcResultStatus, StatusStyle> {
  return {
    pass: {
      label: t('statuses.pass'),
      bg: 'bg-green-100 dark:bg-green-950/50',
      text: 'text-green-700 dark:text-green-400',
      dot: 'bg-green-500',
    },
    warn: {
      label: t('statuses.warn'),
      bg: 'bg-amber-100 dark:bg-amber-950/50',
      text: 'text-amber-700 dark:text-amber-400',
      dot: 'bg-amber-500',
    },
    fail: {
      label: t('statuses.fail'),
      bg: 'bg-red-100 dark:bg-red-950/50',
      text: 'text-red-700 dark:text-red-400',
      dot: 'bg-red-500',
    },
    skip: {
      label: t('statuses.skip'),
      bg: 'bg-slate-100 dark:bg-slate-800',
      text: 'text-slate-600 dark:text-slate-400',
      dot: 'bg-slate-400',
    },
  }
}

export function buildOverallStatusConfig(
  t: TFunction<'qc'>
): Record<QcOverallStatus, StatusStyle> {
  return {
    pass: {
      label: t('overall.pass'),
      bg: 'bg-green-100 dark:bg-green-950/50',
      text: 'text-green-700 dark:text-green-400',
      dot: 'bg-green-500',
    },
    warn: {
      label: t('overall.warn'),
      bg: 'bg-amber-100 dark:bg-amber-950/50',
      text: 'text-amber-700 dark:text-amber-400',
      dot: 'bg-amber-500',
    },
    fail: {
      label: t('overall.fail'),
      bg: 'bg-red-100 dark:bg-red-950/50',
      text: 'text-red-700 dark:text-red-400',
      dot: 'bg-red-500',
    },
  }
}

export function buildDefaultStatusStyle(t: TFunction<'qc'>): StatusStyle {
  return {
    label: t('statuses.unknown'),
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-600 dark:text-slate-400',
    dot: 'bg-slate-400',
  }
}
